import { createContext, useContext, useEffect, useState, useRef, ReactNode } from "react";
import { useFeatureValue, FeaturesReady } from "@growthbook/growthbook-react";

import { useConsoleLog } from "../hooks/consoleLog";
import useUtmData from "../hooks/useUtmData";
import { useUrlContext } from "./urlContext";
import { DEFAULT_FEATURE_KEY, FEATURE_KEY_BY_FLOW } from "../constants/abTests";

import Loader from "../templates/Loader/Loader";

interface GrowthBookContextType {
  abValue: string | number | null;
}

const GrowthBookContext = createContext<GrowthBookContextType | undefined>(undefined);

export const useGrowthbook = (): GrowthBookContextType => {
  const context = useContext(GrowthBookContext);
  if (!context) {
    throw new Error("useGrowthbook must be used within GrowthBookABtestProvider");
  }
  return context;
};

const LoadingFallback = () => {
  return <Loader />;
};

interface SavedGrowthBookData {
  gb_value: string | number;
  saved_at: string;
}

interface ABTestProviderProps {
  children: ReactNode;
}

const ABTestProvider = ({ children }: ABTestProviderProps) => {
  const logger = useConsoleLog();
  const utmData = useUtmData('gbv');

  if ('error' in utmData && !('encodeToUrl' in utmData)) {
    throw new Error('useUtmData returned unexpected format');
  }

  const { encodeToUrl, decodeFromUrl, removeFromUrl } = utmData as {
    encodeToUrl: (data: any, replaceCurrentUrl?: boolean) => string | null;
    decodeFromUrl: (url?: string | null) => any;
    removeFromUrl: () => boolean;
    error: string | null;
  };

  const { currentFlow } = useUrlContext();

  const [abValue, setAbValue] = useState<string | number | null>(null);
  const isInitialized = useRef(false);

  const featureKey = (currentFlow && FEATURE_KEY_BY_FLOW[currentFlow]) || DEFAULT_FEATURE_KEY;
  const growthBookValue = useFeatureValue(featureKey, null) as string | number | null;

  const isExpired = (savedDate: string): boolean => {
    const saved = new Date(savedDate);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setTime(thirtyDaysAgo.getTime() - (30 * 24 * 60 * 60 * 1000));
    return saved <= thirtyDaysAgo;
  };

  const saveNewValue = (value: string | number): void => {
    try {
      const dataToSave: SavedGrowthBookData = {
        gb_value: value,
        saved_at: new Date().toISOString()
      };
      logger.abTest(`Saving new A-B value:`, value);
      encodeToUrl(dataToSave, true);
      setAbValue(value);
    } catch (error) {
      logger.error('Failed to save A-B value:', error);
      setAbValue(value);
    }
  };

  useEffect(() => {
    if (isInitialized.current) return;

    const savedData = decodeFromUrl() as SavedGrowthBookData | null;

    if (savedData && savedData.gb_value && savedData.saved_at) {
      if (isExpired(savedData.saved_at)) {
        logger.abTest(`Saved A-B value expired, removing`);
        removeFromUrl();
        if (growthBookValue) {
          saveNewValue(growthBookValue);
        } else {
          setAbValue(null);
        }
      } else {
        logger.abTest(`Using saved A-B value:`, savedData.gb_value);
        setAbValue(savedData.gb_value);
      }
    } else if (growthBookValue) {
      saveNewValue(growthBookValue);
    } else {
      setAbValue(null);
    }

    isInitialized.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [growthBookValue]);

  useEffect(() => {
    if (!isInitialized.current || !growthBookValue || abValue !== null) return;

    const savedData = decodeFromUrl() as SavedGrowthBookData | null;
    if (!savedData) {
      saveNewValue(growthBookValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [growthBookValue, abValue]);

  const contextValue: GrowthBookContextType = {
    abValue,
  };

  return (
    <GrowthBookContext.Provider value={contextValue}>
      {children}
    </GrowthBookContext.Provider>
  );
};

interface GrowthBookABtestProviderProps {
  children: ReactNode;
}

export const GrowthBookABtestProvider = ({ children }: GrowthBookABtestProviderProps) => (
  <FeaturesReady timeout={1000} fallback={<LoadingFallback />}>
    <ABTestProvider>{children}</ABTestProvider>
  </FeaturesReady>
);